import React, { useMemo } from 'react';
import CruxInsight from '../components/CruxInsight';
import Card from '../design-system/Card';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import useSystemData from '../hooks/useSystemData';
import { generateSystemInsight } from '../utils/insightGen';
import { colors, spacing, fontStack } from '../design-system/tokens';

import Health  from './tabs/Health';
import Money   from './tabs/Money';
import Planner from './tabs/Planner';

/**
 * System module router — Phase 2.
 *   /system               → SystemHome (new clean light UI)
 *   /system/money         → existing Money tab (kept functional)
 *   /system/health        → existing Health tab
 *   /system/planner       → existing Planner tab
 *
 * The old "LIFE ADMIN" dark wrapper has been moved to
 * src/System/_archive/SystemWrapper.old.js.
 */
export default function System() {
  const path = (typeof window !== 'undefined' && window.location.pathname) || '/system';

  if (path === '/system/money')   return <Money />;
  if (path === '/system/health')  return <Health />;
  if (path === '/system/planner') return <Planner />;
  // Anything else under /system falls through to the new home.
  return <SystemHome />;
}

/* ================================================================
   SystemHome
   ================================================================ */

function SystemHome() {
  const { finance, health, planner, loading, error, refresh } = useSystemData();
  const insight = useMemo(
    () => generateSystemInsight(finance, health, planner),
    [finance, health, planner]
  );

  const noData =
    finance.incomeMTD === 0 &&
    finance.expensesMTD === 0 &&
    health.waterMl === 0 &&
    health.exerciseMin === 0 &&
    health.mealsCount === 0 &&
    planner.dueToday === 0 &&
    planner.completedToday === 0 &&
    planner.upcomingThisWeek === 0 &&
    planner.overdue === 0;

  const isInitialLoading = loading && noData && !error;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      {isInitialLoading ? (
        <LoadingState rows={3} label="Loading system insight" />
      ) : (
        <CruxInsight
          state={insight.state}
          actions={insight.actions}
          reasons={insight.reasons}
          confidence={insight.confidence}
          accent={colors.accentSystem}
        />
      )}

      <div className="system-cards-grid">
        <FinanceCard data={finance} loading={loading} />
        <HealthCard data={health} loading={loading} />
        <PlannerCard data={planner} loading={loading} />
        <style>{`
          .system-cards-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            grid-column: 1 / -1;
          }
          @media (max-width: 1024px) {
            .system-cards-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 768px) {
            .system-cards-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>

      <DailyOptimization
        finance={finance}
        health={health}
        planner={planner}
        loading={loading}
      />

      {error && (
        <ErrorState
          title="Some data couldn't load"
          message={error}
          onRetry={refresh}
          inline
        />
      )}
    </div>
  );
}

/* ================================================================
   Module summary cards
   ================================================================ */

function FinanceCard({ data, loading }) {
  const empty = !loading && data.incomeMTD === 0 && data.expensesMTD === 0;
  const balance = data.balance || 0;
  const balanceLabel =
    balance === 0
      ? '£0.00'
      : (balance > 0 ? '+' : '−') + '£' + Math.abs(balance).toFixed(2);

  let topSuggestion = 'Open Money to see this month at a glance.';
  if (typeof data.deltaPct === 'number' && data.deltaPct >= 20) {
    topSuggestion = `Spending up ${data.deltaPct}% vs last month.`;
  } else if (data.balance < 0) {
    topSuggestion = 'Expenses are above income this month.';
  } else if (data.topSpend) {
    topSuggestion = `Largest category: ${data.topSpend.category}.`;
  }

  return (
    <ModuleSummaryCard
      title="Finance"
      accent={colors.accentSystem}
      href="/system/money"
      empty={empty}
      emptyMsg="No transactions yet this month — log one to see your roll-up."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <KvLine label="Income MTD" value={formatGBP(data.incomeMTD)} />
        <KvLine label="Expenses MTD" value={formatGBP(data.expensesMTD)} />
        <KvLine
          label="Balance"
          value={balanceLabel}
          valueColor={balance < 0 ? 'var(--danger)' : colors.accentEnergy}
        />
      </div>
      <BottomSuggestion text={topSuggestion} />
    </ModuleSummaryCard>
  );
}

function HealthCard({ data, loading }) {
  const empty = !loading && data.waterMl === 0 && data.exerciseMin === 0 && data.mealsCount === 0;

  let topSuggestion = "Today's logs look quiet — anything to add?";
  if (data.waterMl < 800) topSuggestion = `Only ${data.waterMl}ml water so far.`;
  else if (data.exerciseMin === 0) topSuggestion = 'No movement logged yet.';
  else if (data.mealsCount < 2) topSuggestion = `Only ${data.mealsCount} meal${data.mealsCount === 1 ? '' : 's'} logged today.`;

  return (
    <ModuleSummaryCard
      title="Health"
      accent={colors.accentSystem}
      href="/system/health"
      empty={empty}
      emptyMsg="Log water, food, or exercise to populate this card."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <KvLine label="Water" value={`${data.waterMl} ml`} />
        <KvLine label="Exercise" value={`${data.exerciseMin} min`} />
        <KvLine label="Meals" value={`${data.mealsCount}`} />
      </div>
      <BottomSuggestion text={topSuggestion} />
    </ModuleSummaryCard>
  );
}

function PlannerCard({ data, loading }) {
  const empty =
    !loading &&
    data.dueToday === 0 &&
    data.completedToday === 0 &&
    data.upcomingThisWeek === 0 &&
    data.overdue === 0;

  let topSuggestion = 'A clean planner — pick the one task that matters most.';
  if (data.overdue > 0) {
    topSuggestion = `${data.overdue} overdue · clear the smallest first.`;
  } else if (data.dueToday >= 5) {
    topSuggestion = `${data.dueToday} due today — consider deferring two.`;
  } else if (data.dueToday > 0 && data.completedToday === 0) {
    topSuggestion = `Start the first of your ${data.dueToday} task${data.dueToday === 1 ? '' : 's'}.`;
  } else if (data.completedToday > 0) {
    topSuggestion = `${data.completedToday} done · keep the momentum.`;
  }

  return (
    <ModuleSummaryCard
      title="Planner"
      accent={colors.accentSystem}
      href="/system/planner"
      empty={empty}
      emptyMsg="Add a task today to see Planner roll-ups here."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <KvLine label="Due today" value={`${data.dueToday}`} />
        <KvLine label="Done today" value={`${data.completedToday}`} />
        <KvLine label="Upcoming (7d)" value={`${data.upcomingThisWeek}`} />
      </div>
      <BottomSuggestion text={topSuggestion} />
    </ModuleSummaryCard>
  );
}

/* ================================================================
   Daily Optimization (full-width)
   ================================================================ */

function DailyOptimization({ finance, health, planner, loading }) {
  const recommendation = useMemo(
    () => buildDailyOptimization(finance, health, planner),
    [finance, health, planner]
  );

  return (
    <Card padding={spacing.lg} accentColor={colors.accentSystem}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: spacing.sm,
          fontFamily: fontStack,
        }}
      >
        <span
          style={{
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.textSecondary,
            fontWeight: 600,
          }}
        >
          Daily Optimization
        </span>
      </div>
      {loading && !recommendation ? (
        <EmptyState message="Reading today's signals…" />
      ) : (
        <p
          style={{
            margin: 0,
            fontSize: 16,
            lineHeight: 1.5,
            color: colors.textPrimary,
            fontFamily: fontStack,
          }}
        >
          {recommendation}
        </p>
      )}
    </Card>
  );
}

function buildDailyOptimization(finance, health, planner) {
  const f = finance || {};
  const h = health || {};
  const p = planner || {};

  if (p.overdue >= 3) {
    return `You have ${p.overdue} overdue tasks and ${p.dueToday} due today. Clear two of the smallest before noon — momentum compounds.`;
  }
  if (p.dueToday >= 5) {
    return `You have ${p.dueToday} tasks due today. Pick the three that move the week forward and defer the rest.`;
  }
  if (typeof f.deltaPct === 'number' && f.deltaPct >= 25) {
    return `Spending is up ${f.deltaPct}% versus last month. Skim the Money tab for the categories driving it before you commit to anything new.`;
  }
  if (f.balance < 0 && f.expensesMTD > 0) {
    return 'Expenses are running ahead of income this month. A 15-minute review of the top three categories tonight will give you a cleaner second half.';
  }
  if (h.waterMl < 800 && p.dueToday > 0) {
    return `Only ${h.waterMl}ml water and ${p.dueToday} task${p.dueToday === 1 ? '' : 's'} due — drink a glass before your next deep work block.`;
  }
  if (h.exerciseMin === 0 && p.completedToday >= 2) {
    return 'Tasks are flowing — protect a 15-minute walk before the next meeting block to keep the energy steady.';
  }
  if (p.dueToday > 0 && p.completedToday === 0) {
    return `Nothing logged as done yet. Start the easiest of your ${p.dueToday} task${p.dueToday === 1 ? '' : 's'} — completion creates momentum.`;
  }
  if (p.completedToday > 0 && p.dueToday === 0) {
    return `${p.completedToday} task${p.completedToday === 1 ? '' : 's'} already complete and nothing else due. Use the slack to plan tomorrow with intention.`;
  }
  return 'Most areas look balanced. A quiet day is a good day to invest 30 minutes in something you have been deferring.';
}

/* ================================================================
   shared bits
   ================================================================ */

function ModuleSummaryCard({
  title, accent, href, children, empty, emptyMsg,
}) {
  const handleClick = () => {
    if (href) window.location.href = href;
  };
  return (
    <Card
      accentColor={accent}
      onClick={handleClick}
      padding={spacing.lg}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <h3
        style={{
          margin: 0,
          marginBottom: spacing.sm,
          fontSize: 16,
          fontWeight: 600,
          color: colors.textPrimary,
          fontFamily: fontStack,
        }}
      >
        {title}
      </h3>
      {empty ? (
        <EmptyState message={emptyMsg} />
      ) : (
        children
      )}
    </Card>
  );
}

function KvLine({ label, value, valueColor }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 8,
        fontFamily: fontStack,
      }}
    >
      <span style={{ fontSize: 13, color: colors.textSecondary }}>{label}</span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: valueColor || colors.textPrimary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function BottomSuggestion({ text }) {
  return (
    <div
      style={{
        marginTop: 'auto',
        paddingTop: 8,
        borderTop: `1px solid ${colors.border}`,
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fontStack,
        lineHeight: 1.4,
      }}
    >
      {text}
    </div>
  );
}

function formatGBP(n) {
  const v = Number(n) || 0;
  return '£' + v.toFixed(2);
}

